import React, { useState, useCallback } from 'react';
import axiosInstance from '../api/axiosInstance';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Paper,
    Divider,
    Chip
} from '@mui/material';
import { CheckCircleOutline, NewReleases } from '@mui/icons-material';

const FollowedTimelines = ({ onTimelineSelect }) => {
    const [timelines, setTimelines] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    const fetchFollowedTimelines = useCallback(async () => {
        setLoading(true);
        setError('');
        setTimelines([]);
        try {
            const response = await axiosInstance.get('/api/user/followed-timelines');
            setTimelines(response.data);
        } catch (err) {
            console.error("Error fetching followed timelines:", err);
            if (err.response && err.response.status === 429) {
                setError(`Rate limit exceeded. Please try again later. (Next reset: ${new Date(err.response.data.retryAfter).toLocaleString()})`);
            } else {
                setError('Failed to fetch followed timelines. Please try again.');
            }
        } finally {
            setLoading(false);
            setHasSearched(true);
        }
    }, []);

    return (
        <Paper elevation={2} sx={{ mt: 4, p: 2 }}>
            <Typography variant="h6" gutterBottom>
                Followed Timelines
            </Typography>
            <Box sx={{ mb: 2 }}>
                <Button
                    variant="contained"
                    onClick={fetchFollowedTimelines}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleOutline />}
                >
                    Check for Updates
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            {timelines.length > 0 && (
                <List>
                    {timelines.map((timeline, index) => (
                        <React.Fragment key={timeline._id}>
                            <ListItem
                                button
                                onClick={() => onTimelineSelect && onTimelineSelect(timeline)}
                            >
                                <ListItemIcon>
                                    {timeline.hasUpdates ? <NewReleases color="primary" /> : <CheckCircleOutline color="disabled" />}
                                </ListItemIcon>
                                <ListItemText
                                    primary={timeline.applicationStream || 'Unnamed Timeline'}
                                    secondary={`Last updated: ${new Date(timeline.updatedAt).toLocaleDateString()}`}
                                />
                                {timeline.hasUpdates && (
                                    <Chip label="New Updates" color="primary" size="small" />
                                )}
                            </ListItem>
                            {index < timelines.length - 1 && <Divider variant="inset" component="li" />}
                        </React.Fragment>
                    ))}
                </List>
            )}

            {!loading && hasSearched && !error && timelines.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    No timelines are being followed.
                </Alert>
            )}
        </Paper>
    );
};

export default FollowedTimelines;